import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { aiSupportApi } from "src/_api/aiSupportApi";
import { CHAT_SIGNALS } from "src/_modules/AiSupportChat/core/chatReducer";
import useAiEligibility from "src/_modules/AiSupportChat/hooks/useAiEligibility";
import useAiChatPanel, {
  PANEL_VIEWS,
} from "src/_modules/AiSupportChat/hooks/useAiChatPanel";
import useAiChat from "src/_modules/AiSupportChat/hooks/useAiChat";

const AiChatContext = createContext(null);

/**
 * Holds the chat state above the panel (plan §6.4): closing the panel or
 * changing routes never unmounts it, so a streaming answer keeps going.
 * `AiChatLauncher` mounts it under the eligibility gate, keyed by profile id,
 * so a different user always starts from a fresh provider.
 *
 * It also acts on the reducer's one-shot signals, whether or not the panel
 * is open: `goHome` (855) shows home and refetches the list;
 * `refetchEligibility` (851: the launcher and this provider go away when
 * `enabled` turns false; 857: a fresh `maxMessageChars`) refetches
 * `/eligibility`. And once `meta` names the conversation a draft created,
 * the draft view becomes that conversation's view (so it is remembered
 * across a reload).
 *
 * Context value:
 * - `eligibility`: the raw `/eligibility` query result (`data.maxMessageChars`,
 *   `data.retentionDays`, `data.support.whatsappUrl`, `refetch`);
 * - `isAiDeveloper`: show the debug toggle / debug data;
 * - `debugOn` / `toggleDebug`: the header's debug toggle (AI developers);
 *   off on every page load, and kept while the panel closes and reopens;
 * - `panel`: `useAiChatPanel` (open state + view);
 * - `chat`: `useAiChat` (conversation state + actions).
 */
export function AiChatProvider({ profileId, children }) {
  const { eligibility, isAiDeveloper } = useAiEligibility();
  const panel = useAiChatPanel(profileId);
  const chat = useAiChat({
    maxMessageChars: eligibility.data?.maxMessageChars,
  });
  const appDispatch = useDispatch();
  const [debugOn, setDebugOn] = useState(false);
  const toggleDebug = useCallback(() => setDebugOn((on) => !on), []);

  const { signal } = chat.state;
  const { clearSignal } = chat;
  const { view, showHome, showConversation } = panel;
  useEffect(() => {
    if (signal === CHAT_SIGNALS.GO_HOME) {
      showHome();
      appDispatch(aiSupportApi.util.invalidateTags(["AiConversations"]));
      clearSignal();
    } else if (signal === CHAT_SIGNALS.REFETCH_ELIGIBILITY) {
      // A tag, not `eligibility.refetch()`: that throws on a skipped query
      appDispatch(aiSupportApi.util.invalidateTags(["AiEligibility"]));
      clearSignal();
    }
  }, [signal, showHome, clearSignal, appDispatch]);

  // Every way into the draft view also starts a reducer draft (AiChatPanel),
  // so a draft view over a real conversation means `meta` just named it
  const { conversation } = chat.state;
  const namedId =
    conversation && !conversation.isDraft ? conversation.id : null;
  useEffect(() => {
    if (view === PANEL_VIEWS.DRAFT && namedId) showConversation(namedId);
  }, [view, namedId, showConversation]);

  const value = useMemo(
    () => ({
      eligibility,
      isAiDeveloper,
      debugOn: isAiDeveloper && debugOn,
      toggleDebug,
      panel,
      chat,
    }),
    [eligibility, isAiDeveloper, debugOn, toggleDebug, panel, chat]
  );

  return (
    <AiChatContext.Provider value={value}>{children}</AiChatContext.Provider>
  );
}

/** The chat context; only valid under `AiChatProvider`. */
export function useAiChatContext() {
  const context = useContext(AiChatContext);
  if (!context) {
    throw new Error("useAiChatContext must be used inside AiChatProvider");
  }
  return context;
}
